// Plain-text views of a forum post's markdown, for headlines and previews.
import type { ClubAPI } from "@shared/types/api";

// Marker characters are dropped; backslash-escaped ones (typed literally) are kept.
/** A thread's headline: its first line with markdown syntax stripped. */
export function threadTitle(body: string): string {
  const firstLine = body.split("\n").map((l) => l.trim()).find(Boolean) ?? "";
  return firstLine
    .replace(/^#{1,6}\s+/, "")
    .replace(/^[-*>]\s+/, "")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\\(.)|[*_`~]/g, (_, escaped) => escaped ?? "");
}

/** Everything after a thread's first line, as plain-ish text for an excerpt. */
export function threadExcerpt(body: string): string {
  const lines = body.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.slice(1).join(" ").replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1").replace(/\\(.)|[*_`~#>]/g, (_, escaped) => escaped ?? "");
}

/** A thread's headline: its title, or (posts from before titles existed) its first line. */
export function postHeadline(post: Pick<ClubAPI.ClubPost, "title" | "body">): string {
  return post.title || threadTitle(post.body);
}

/** "now", "5m", "3h", "2d", or a short date for anything older than a week. */
export function relativeTime(iso: string, now = Date.now()): string {
  const mins = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
